import { EnvType } from "../services";
import { IApiClient } from "../api-client";
import { RegisterAndParseFileRequest } from "../Types/register-and-parse-file-request";
import { UserDataStatus } from "../Types/user-data-status";
import { UserDataType } from "../Types/user-data-type";
import { UserDataCustomType } from "../Types/user-data-custom-type";
import { AddFileUserDataRequest } from "../Types/add-file-user-data-request";
import { GetSmilCustomRequest } from "../Types/get-smil-custom-request";
import { GetFilesResponse } from "../Types/get-files-response";
import { GenericResponseStd } from "../Types/generic-response-std";
import { SyncMediaFileResponse } from "../Types/sync-media-file-response";
import { UploadMediaInfoReponse } from "../Types/upload-media-info-reponse";
import { GetMediaResourceResponse } from "../Types/get-media-resource-response";
import { GetNleTimelineInfos } from "../Types/get-nle-timeline-infos";
import { GetStreamingSmilByObjectIdResponse } from "../Types/get-streaming-smil-by-object-id-response";
import { GetSubtitleParsedInfosResponse } from "../Types/get-subtitle-parsed-infos-response";
import { GetSubtitlesCheckerReportResponse } from "../Types/get-subtitles-checker-report-response";
import { GetSubtitlesCheckerTemplatesResponse } from "../Types/get-subtitles-checker-templates-response";
import { GetSubtitlesResponse } from "../Types/get-subtitles-response";
import { StoragePathAliasType } from "../Types/storage-path-alias-type";
import { FileCategory } from "../Types/file-category";

interface IMediaApiClient {
  GetFilesByObjectId(objectId: number): Promise<GetFilesResponse>;
  GetChildrenFilesByObjectId(objectId: number): Promise<GetFilesResponse>;
  RegisterAndParseFile(request: RegisterAndParseFileRequest): Promise<GenericResponseStd>;
  UploadMediaFile(
    formData: FormData,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<UploadMediaInfoReponse>;
  DownloadMediaFile(objectId: number, fileId: number, authKey: string): string;
  DownloadMediaFileByCategory(objectId: number, category: FileCategory, authKey: string): string;
  DownloadShareMediaFile(mediaKey: string): string;
  DeleteMediaFile(fileId: number): Promise<GenericResponseStd>;
  SyncMediaFile(fileId: number): Promise<SyncMediaFileResponse>;
  SyncMediaFileMetadata(fileId: number): Promise<SyncMediaFileResponse>;
  ParseMediaFile(fileId: number): Promise<SyncMediaFileResponse>;
  AddUserDataOnMediaFile(request: AddFileUserDataRequest): Promise<GenericResponseStd>;
  GetStreamingSmilByObjectId(objectId: number): Promise<GetStreamingSmilByObjectIdResponse>;
  GetStreamingSmilByObjectIdFilteredByEpisodeNumber(
    objectId: number,
    episodeNumber: number
  ): Promise<GetStreamingSmilByObjectIdResponse>;
  GetStreamingSmilWithCustoms(request: GetSmilCustomRequest): Promise<GetStreamingSmilByObjectIdResponse>;
  GetStreamingSmilByShareMediaKey(mediaKey: string): Promise<GetStreamingSmilByObjectIdResponse>;
  GetMediaResources(): Promise<GetMediaResourceResponse>;
  GetSubtitleParsedInfosByObjectId(objectId: number): Promise<GetSubtitleParsedInfosResponse>;
  GetWebVTTSubtitle(objectId: number, cultureId: number): string;
  GetSubtitleFileAsMetadata(fileId: number): Promise<GetSubtitlesResponse>;
  GetSubtitleCheckTemplates(): Promise<GetSubtitlesCheckerTemplatesResponse>;
  GetSubtitleCheckReport(fileId: number, templateId: number): Promise<GetSubtitlesCheckerReportResponse>;
  GetImagePreviewer(objectId: number, authKey: string): string;
  GetFileImagePreviewer(fileId: number, authKey: string): string;
  GetShareImagePreviewer(mediaKey: string): string;
  GetScreenshotFromAssetVideoProxy(objectId: number, frame: number, authKey: string): string;
  GetScreenshotFromAssetVideoHires(objectId: number, frame: number, authKey: string): string;
  GetNleTimeline(objectId: number): Promise<GetNleTimelineInfos>;
  GetNleTimelineWithAlias(objectId: number, pathAlias: StoragePathAliasType): Promise<GetNleTimelineInfos>;
  SetPlayLog(objectId: number): Promise<GenericResponseStd>;
}

export default class MediaApiClient implements IMediaApiClient {
  readonly servicePort = 9505;
  readonly serviceBaseUrl = "media-service/api";

  mediaApiClient: IApiClient;

  constructor(mediaApiClient: IApiClient, envType: EnvType) {
    this.mediaApiClient = mediaApiClient;
    if (envType === "local") this.mediaApiClient.client.defaults.baseURL += `${this.servicePort}/`;
  }

  async GetFilesByObjectId(objectId: number): Promise<GetFilesResponse> {
    return await this.mediaApiClient.get(`${this.serviceBaseUrl}/media-infos/object/${objectId}`);
  }
  async GetChildrenFilesByObjectId(objectId: number): Promise<GetFilesResponse> {
    return await this.mediaApiClient.get(`${this.serviceBaseUrl}/media-infos/object/${objectId}/children`);
  }
  async RegisterAndParseFile(request: RegisterAndParseFileRequest): Promise<GenericResponseStd> {
    return await this.mediaApiClient.post(`${this.serviceBaseUrl}/media-infos`, request);
  }
  async UploadMediaFile(
    formData: FormData,
    abortSignal?: AbortSignal,
    onUploadProgressCallback?: (progressEvent: any) => void
  ): Promise<UploadMediaInfoReponse> {
    return await this.mediaApiClient.postUploadFormData(
      `${this.serviceBaseUrl}/media-infos/object`,
      formData,
      abortSignal,
      onUploadProgressCallback
    );
  }
  DownloadMediaFile(objectId: number, fileId: number, authKey: string): string {
    return (
      this.mediaApiClient.client.defaults.baseURL +
      `${this.serviceBaseUrl}/media-infos/object/${objectId}/${fileId}/${authKey}`
    );
  }
  DownloadMediaFileByCategory(objectId: number, category: FileCategory, authKey: string): string {
    return (
      this.mediaApiClient.client.defaults.baseURL +
      `${this.serviceBaseUrl}/media-infos/object/${objectId}/${category}/${authKey}`
    );
  }
  DownloadShareMediaFile(mediaKey: string): string {
    return (
      this.mediaApiClient.client.defaults.baseURL + `${this.serviceBaseUrl}/media-infos/share/${mediaKey}/download`
    );
  }
  async DeleteMediaFile(fileId: number): Promise<GenericResponseStd> {
    return await this.mediaApiClient.delete(`${this.serviceBaseUrl}/media-infos/${fileId}`);
  }
  async SyncMediaFile(fileId: number): Promise<SyncMediaFileResponse> {
    return await this.mediaApiClient.put(`${this.serviceBaseUrl}/media-infos/sync/${fileId}`);
  }
  async SyncMediaFileMetadata(fileId: number): Promise<SyncMediaFileResponse> {
    return await this.mediaApiClient.put(`${this.serviceBaseUrl}/media-infos/sync-metadata/${fileId}`);
  }
  async ParseMediaFile(fileId: number): Promise<SyncMediaFileResponse> {
    return await this.mediaApiClient.put(`${this.serviceBaseUrl}/media-infos/parse/${fileId}`);
  }
  async AddUserDataOnMediaFile(request: AddFileUserDataRequest): Promise<GenericResponseStd> {
    return await this.mediaApiClient.put(`${this.serviceBaseUrl}/media-infos/user-data`, request);
  }
  async GetStreamingSmilByObjectId(objectId: number): Promise<GetStreamingSmilByObjectIdResponse> {
    return await this.mediaApiClient.get(`${this.serviceBaseUrl}/streaming/smil/${objectId}`);
  }
  async GetStreamingSmilByObjectIdFilteredByEpisodeNumber(
    objectId: number,
    episodeNumber: number
  ): Promise<GetStreamingSmilByObjectIdResponse> {
    return await this.mediaApiClient.get(`${this.serviceBaseUrl}/streaming/smil/${objectId}/episodes/${episodeNumber}`);
  }
  async GetStreamingSmilWithCustoms(request: GetSmilCustomRequest): Promise<GetStreamingSmilByObjectIdResponse> {
    return await this.mediaApiClient.post(`${this.serviceBaseUrl}/streaming/smil/customs`, request);
  }
  async GetStreamingSmilByShareMediaKey(mediaKey: string): Promise<GetStreamingSmilByObjectIdResponse> {
    return await this.mediaApiClient.get(`${this.serviceBaseUrl}/streaming/smil/shares/${mediaKey}`);
  }
  async GetMediaResources(): Promise<GetMediaResourceResponse> {
    return await this.mediaApiClient.get(`${this.serviceBaseUrl}/media-resources`);
  }
  async GetSubtitleParsedInfosByObjectId(objectId: number): Promise<GetSubtitleParsedInfosResponse> {
    return await this.mediaApiClient.get(`${this.serviceBaseUrl}/parser/subtitle/${objectId}`);
  }
  GetWebVTTSubtitle(objectId: number, cultureId: number): string {
    return this.mediaApiClient.client.defaults.baseURL + `parser/subtitle/${objectId}/vtt/${cultureId}`;
  }
  async GetSubtitleFileAsMetadata(fileId: number): Promise<GetSubtitlesResponse> {
    return await this.mediaApiClient.get(`${this.serviceBaseUrl}/parser/subtitle/metadata/${fileId}`);
  }
  async GetSubtitleCheckTemplates(): Promise<GetSubtitlesCheckerTemplatesResponse> {
    return await this.mediaApiClient.get(`${this.serviceBaseUrl}/parser/subtitle/checker/templates`);
  }
  async GetSubtitleCheckReport(fileId: number, templateId: number): Promise<GetSubtitlesCheckerReportResponse> {
    return await this.mediaApiClient.get(
      `${this.serviceBaseUrl}/parser/subtitle/checker/report/${fileId}/${templateId}`
    );
  }
  GetImagePreviewer(objectId: number, authKey: string): string {
    return (
      this.mediaApiClient.client.defaults.baseURL + `${this.serviceBaseUrl}/image-previewer/${objectId}/${authKey}`
    );
  }
  GetFileImagePreviewer(fileId: number, authKey: string): string {
    return (
      this.mediaApiClient.client.defaults.baseURL + `${this.serviceBaseUrl}/image-previewer/file/${fileId}/${authKey}`
    );
  }
  GetShareImagePreviewer(mediaKey: string): string {
    return this.mediaApiClient.client.defaults.baseURL + `${this.serviceBaseUrl}/image-previewer/share/${mediaKey}`;
  }
  GetScreenshotFromAssetVideoProxy(objectId: number, frame: number, authKey: string): string {
    return (
      this.mediaApiClient.client.defaults.baseURL +
      `${this.serviceBaseUrl}/image-grabber/${objectId}/${frame}/${authKey}`
    );
  }
  GetScreenshotFromAssetVideoHires(objectId: number, frame: number, authKey: string): string {
    return (
      this.mediaApiClient.client.defaults.baseURL +
      `${this.serviceBaseUrl}/image-grabber/hires/${objectId}/${frame}/${authKey}`
    );
  }
  async GetNleTimeline(objectId: number): Promise<GetNleTimelineInfos> {
    return await this.mediaApiClient.get(`${this.serviceBaseUrl}/nle/timeline/${objectId}`);
  }
  async GetNleTimelineWithAlias(objectId: number, pathAlias: StoragePathAliasType): Promise<GetNleTimelineInfos> {
    return await this.mediaApiClient.get(`${this.serviceBaseUrl}/nle/timeline/${objectId}/${pathAlias}`);
  }
  async SetPlayLog(objectId: number): Promise<GenericResponseStd> {
    return await this.mediaApiClient.put(`${this.serviceBaseUrl}/play/${objectId}`);
  }

  //------Utils------
  GetApiUrl(): string {
    return this.mediaApiClient.client.defaults.baseURL + this.serviceBaseUrl;
  }
}
